import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Modal,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

const API_BASE = "/api/bdb/pet";
const CITYFARM_URL = "https://www.cityfarmgame.com/";

/* ============================================================
   IMAGES
   NOTE:
   You only sent 1 new walk frame.
   So walk2 and walk3 reuse walk1 for now.
   When you send real walk2 and walk3, replace them here.
============================================================ */

const dogImages = {
  idle: "https://res.cloudinary.com/dekoxefky/image/upload/v1780870756/dog_walk_1.png_xhd04p.png",
  walk1: "https://res.cloudinary.com/dekoxefky/image/upload/v1780870756/dog_walk_1.png_xhd04p.png",
  walk2: "https://res.cloudinary.com/dekoxefky/image/upload/v1780870756/dog_walk_1.png_xhd04p.png",
  walk3: "https://res.cloudinary.com/dekoxefky/image/upload/v1780870756/dog_walk_1.png_xhd04p.png",
  eat: "https://res.cloudinary.com/dekoxefky/image/upload/v1780870749/dog_eat.png_t8b4uq.png",
  drink: "https://res.cloudinary.com/dekoxefky/image/upload/v1780870751/dog_drink_d8wpqw.png",
  play: "https://res.cloudinary.com/dekoxefky/image/upload/v1780870752/dog_play.png_kunios.png",
  sleep: "https://res.cloudinary.com/dekoxefky/image/upload/v1780870750/dog_sleep.png_ifeofv.png",
  weak: "https://res.cloudinary.com/dekoxefky/image/upload/v1780870746/dog_fallen.png_dsegnx.png",
  fallen: "https://res.cloudinary.com/dekoxefky/image/upload/v1780870746/dog_fallen.png_dsegnx.png",
};

const ACTION_VIEW_TIME = {
  eat: 4200,
  drink: 4200,
  play: 3800,
  sleep: 5000,
};

const benefitLevels = [
  { level: "1-9", benefit: "+1% Mini-Bone bonus", daily: "Base daily reward" },
  { level: "10-14", benefit: "+2% Mini-Bone bonus", daily: "Higher daily reward" },
  { level: "15-19", benefit: "+2% Mini-Bone + Energy bonus", daily: "Higher daily reward" },
  { level: "20-29", benefit: "+3% Mini-Bone + Walk reduction", daily: "Improved reward" },
  { level: "30-49", benefit: "Adult pet bonuses", daily: "Strong reward" },
  { level: "50+", benefit: "Legendary pet bonuses", daily: "Best reward tier" },
];

/* ============================================================
   HELPERS
============================================================ */

function getPetImage(pet, animation, walkFrame = 1) {
  if (pet?.status === "fallen") return dogImages.fallen;
  if (pet?.status === "weak") return dogImages.weak;

  if (animation === "eat") return dogImages.eat;
  if (animation === "drink") return dogImages.drink;
  if (animation === "play") return dogImages.play;
  if (animation === "sleep") return dogImages.sleep;

  if (animation === "walk") {
    if (walkFrame === 1) return dogImages.walk1;
    if (walkFrame === 2) return dogImages.walk2;
    return dogImages.walk3;
  }

  return dogImages.idle;
}

function preloadImages() {
  Object.values(dogImages).forEach((src) => {
    const img = new Image();
    img.src = src;
  });
}

function getToken() {
  return sessionStorage.getItem("token");
}

function petApi() {
  return axios.create({
    baseURL: API_BASE,
    headers: { token: getToken() },
  });
}

function fmtNumber(value) {
  return Number(value || 0).toLocaleString(undefined, {
    maximumFractionDigits: 3,
  });
}

function statusColor(status) {
  if (status === "alive") return "#00e676";
  if (status === "weak") return "#ffb300";
  if (status === "fallen") return "#ff1744";
  return "#ffb300";
}

function statusText(status) {
  if (status === "alive") return "Alive";
  if (status === "weak") return "Weak";
  if (status === "fallen") return "Fallen";
  return status || "Unknown";
}

function stageText(stage) {
  if (stage === "puppy") return "Puppy";
  if (stage === "young") return "Young";
  if (stage === "adult") return "Adult";
  if (stage === "veteran") return "Veteran";
  if (stage === "legendary") return "Legendary";
  return stage || "Puppy";
}

function getBarColor(value) {
  const safeValue = Number(value || 0);
  if (safeValue > 65) return "#00e676";
  if (safeValue > 35) return "#ffb300";
  return "#ff1744";
}

function StatBar({ label, value, icon }) {
  const safeValue = Math.max(0, Math.min(100, Number(value || 0)));

  return (
    <Box sx={{ width: "100%", mb: 0.8 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
        <Typography sx={{ color: "white", fontSize: 11 }}>
          {icon} {label}
        </Typography>
        <Typography sx={{ color: "#ffcc80", fontSize: 11, fontWeight: 900 }}>
          {Math.round(safeValue)}%
        </Typography>
      </Box>

      <LinearProgress
        variant="determinate"
        value={safeValue}
        sx={{
          height: 7,
          borderRadius: 10,
          backgroundColor: "rgba(255,255,255,0.16)",
          "& .MuiLinearProgress-bar": {
            borderRadius: 10,
            backgroundColor: getBarColor(safeValue),
          },
        }}
      />
    </Box>
  );
}

function InfoCard({ title, children, sx = {} }) {
  return (
    <Box
      sx={{
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))",
        border: "1px solid rgba(255,152,0,0.25)",
        borderRadius: 2.5,
        p: 1.1,
        ...sx,
      }}
    >
      <Typography
        sx={{
          color: "#ffb300",
          mb: 0.75,
          fontWeight: 900,
          fontSize: 13,
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function SmallRow({ label, value }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        gap: 1,
        mb: 0.35,
      }}
    >
      <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: 12 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          color: "white",
          fontSize: 12,
          fontWeight: 900,
          textAlign: "right",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function getCurrentBenefitByLevel(level) {
  const lvl = Number(level || 0);

  if (lvl >= 50) return benefitLevels[5];
  if (lvl >= 30) return benefitLevels[4];
  if (lvl >= 20) return benefitLevels[3];
  if (lvl >= 15) return benefitLevels[2];
  if (lvl >= 10) return benefitLevels[1];
  return benefitLevels[0];
}

/* ============================================================
   MAIN COMPONENT
============================================================ */

export default function PetOverlay() {
  const [petData, setPetData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openPanel, setOpenPanel] = useState(false);
  const [nickname, setNickname] = useState("");
  const [message, setMessage] = useState(null);
  const [animation, setAnimation] = useState("idle");
  const [walkFrame, setWalkFrame] = useState(1);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  const animationTimerRef = useRef(null);

  const pet = petData?.pet;
  const cityFarm = petData?.cityFarm;
  const bdb = petData?.bdb;
  const benefit = petData?.benefit;
  const cooldowns = petData?.cooldowns;

  const displayName = useMemo(() => {
    if (!pet) return "My Puppy";
    return pet.nickname || pet.birthName || "My Puppy";
  }, [pet]);

  const waterQty = cityFarm?.inventory?.Water ?? 0;
  const foodQty = cityFarm?.inventory?.Croquettes ?? 0;
  const claimAmount = benefit?.dailyClaim?.miniHueso || 0;
  const canUseActions = pet?.status !== "fallen";
  const currentLevelBenefit = getCurrentBenefitByLevel(pet?.level || 1);

  const clearAnimationTimer = () => {
    if (animationTimerRef.current) {
      clearTimeout(animationTimerRef.current);
      animationTimerRef.current = null;
    }
  };

  const setTimedAnimation = (nextAnimation, duration) => {
    clearAnimationTimer();
    setAnimation(nextAnimation);
    animationTimerRef.current = setTimeout(() => {
      setAnimation("idle");
    }, duration || 3000);
  };

  const refreshPet = async ({ silent = false, keepMessage = true } = {}) => {
    try {
      if (!silent) setLoading(true);

      const res = await petApi().get("/me");

      setPetData(res.data);
      setNickname(res.data?.pet?.nickname || "");
      setLastUpdated(new Date());

      if (!keepMessage) setMessage(null);

      return res.data;
    } catch (err) {
      console.error("[PET FRONT] load error:", err);

      if (!silent) {
        setMessage({
          type: "error",
          text:
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Could not load your puppy.",
        });
      }

      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const updateFromResponseThenRefresh = async (resData) => {
    setPetData((prev) => ({
      ...prev,
      ...resData,
      pet: resData?.pet || prev?.pet,
      benefit: resData?.benefit || prev?.benefit,
      cityFarm: {
        ...(prev?.cityFarm || {}),
        ...(resData?.cityFarm || {}),
      },
      bdb: {
        ...(prev?.bdb || {}),
        ...(resData?.bdb || {}),
      },
    }));

    await refreshPet({
      silent: true,
      keepMessage: true,
    });
  };

  const runAction = async (action, actionAnimation) => {
    try {
      setLoading(true);
      setMessage(null);

      setTimedAnimation(
        actionAnimation,
        ACTION_VIEW_TIME[actionAnimation] || 3200
      );

      const res = await petApi().post(`/${action}`);

      setMessage({
        type: "success",
        text: res.data?.message || "Action completed successfully.",
      });

      await updateFromResponseThenRefresh(res.data);
    } catch (err) {
      console.error("[PET FRONT] action error:", err);

      const data = err?.response?.data;

      setMessage({
        type: "error",
        text: data?.cooldown?.text
          ? `${data?.message || "This action is on cooldown."} Wait ${data.cooldown.text}`
          : data?.message || data?.error || "Action failed.",
      });

      await refreshPet({
        silent: true,
        keepMessage: true,
      });

      clearAnimationTimer();
      setAnimation("idle");
    } finally {
      setLoading(false);
    }
  };

  const feedPet = () => runAction("feed", "eat");
  const waterPet = () => runAction("water", "drink");
  const playPet = () => runAction("play", "play");
  const claimDaily = () => runAction("claim-daily", "play");

  const saveNickname = async () => {
    try {
      const cleanNickname = String(nickname || "").trim();

      if (!cleanNickname || cleanNickname.length < 2) {
        setMessage({
          type: "error",
          text: "Nickname must have at least 2 characters.",
        });
        return;
      }

      setLoading(true);
      setMessage(null);

      const res = await petApi().post("/nickname", {
        nickname: cleanNickname,
      });

      setMessage({
        type: "success",
        text: res.data?.message || "Nickname updated.",
      });

      await updateFromResponseThenRefresh(res.data);
    } catch (err) {
      setMessage({
        type: "error",
        text:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Could not update nickname.",
      });
    } finally {
      setLoading(false);
    }
  };

  const revivePet = async (paymentType) => {
    try {
      setLoading(true);
      setMessage(null);

      setTimedAnimation("play", 3000);

      const res = await petApi().post("/revive", { paymentType });

      setMessage({
        type: "success",
        text: res.data?.message || "Puppy revived successfully.",
      });

      await updateFromResponseThenRefresh(res.data);
    } catch (err) {
      setMessage({
        type: "error",
        text:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Could not revive your puppy.",
      });

      await refreshPet({
        silent: true,
        keepMessage: true,
      });

      clearAnimationTimer();
      setAnimation("idle");
    } finally {
      setLoading(false);
    }
  };

  const openCityFarm = () => {
    window.open(CITYFARM_URL, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    preloadImages();
    refreshPet();

    const interval = setInterval(() => {
      refreshPet({
        silent: true,
        keepMessage: true,
      });
    }, 60000);

    return () => {
      clearInterval(interval);
      clearAnimationTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const frameInterval = setInterval(() => {
      setWalkFrame((prev) => (prev >= 3 ? 1 : prev + 1));
    }, 450);

    return () => clearInterval(frameInterval);
  }, []);

  if (!pet) return null;

  const floatingImage = getPetImage(
    pet,
    animation === "idle" ? "walk" : animation,
    walkFrame
  );

  const panelImage = getPetImage(pet, animation, walkFrame);

  return (
    <>
      {/* ======================================================
          FLOATING SPHERE DOG
      ====================================================== */}
      <Tooltip title="Open puppy panel" arrow>
        <Box
          onClick={() => setOpenPanel(true)}
          sx={{
            position: "fixed",
            left: { xs: 10, sm: 25 },
            bottom: { xs: 54, sm: 62 },
            zIndex: 9999,
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <Box
            sx={{
              mb: 0.75,
              px: 1.2,
              py: 0.45,
              borderRadius: "999px",
              border: "1px solid rgba(255,152,0,0.8)",
              background: "rgba(0,0,0,0.72)",
              backdropFilter: "blur(8px)",
              color: "white",
              fontSize: 12,
              fontWeight: 800,
              textAlign: "center",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.7,
              boxShadow: "0 0 12px rgba(0,0,0,0.45)",
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: statusColor(pet.status),
                boxShadow: `0 0 10px ${statusColor(pet.status)}`,
              }}
            />
            {pet.status === "fallen"
              ? "I need help"
              : pet.status === "weak"
              ? "I feel weak"
              : animation === "eat"
              ? "Eating"
              : animation === "drink"
              ? "Drinking"
              : animation === "play"
              ? "Playing"
              : displayName}
          </Box>

          <Box
            sx={{
              width: { xs: 118, sm: 138 },
              height: { xs: 118, sm: 138 },
              borderRadius: "50%",
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                "radial-gradient(circle at 30% 25%, rgba(255,255,255,0.24), rgba(255,255,255,0.08) 25%, rgba(0,170,255,0.08) 55%, rgba(255,152,0,0.16) 100%)",
              border: "2px solid rgba(255,255,255,0.24)",
              boxShadow:
                "0 0 25px rgba(0,160,255,0.22), 0 0 18px rgba(255,152,0,0.35), inset 0 0 20px rgba(255,255,255,0.08)",
              backdropFilter: "blur(6px)",
              overflow: "hidden",
              animation:
                pet.status === "fallen"
                  ? "sphereStill 2s ease-in-out infinite"
                  : "sphereFloat 4.2s ease-in-out infinite",
              "@keyframes sphereFloat": {
                "0%": { transform: "translateY(0px)" },
                "50%": { transform: "translateY(-10px)" },
                "100%": { transform: "translateY(0px)" },
              },
              "@keyframes sphereStill": {
                "0%": { transform: "translateY(0px)" },
                "50%": { transform: "translateY(-3px)" },
                "100%": { transform: "translateY(0px)" },
              },
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 14,
                left: 18,
                width: 24,
                height: 24,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.16)",
                filter: "blur(2px)",
              }}
            />

            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                animation:
                  animation === "idle"
                    ? "dogDrift 2.6s ease-in-out infinite"
                    : animation === "eat" || animation === "drink"
                    ? "dogActionNod 0.7s ease-in-out infinite"
                    : animation === "play"
                    ? "dogPlayHop 0.55s ease-in-out infinite"
                    : "dogDrift 2.6s ease-in-out infinite",
                "@keyframes dogDrift": {
                  "0%": { transform: "translateX(-6px) translateY(3px) rotate(-2deg)" },
                  "50%": { transform: "translateX(6px) translateY(-1px) rotate(2deg)" },
                  "100%": { transform: "translateX(-6px) translateY(3px) rotate(-2deg)" },
                },
                "@keyframes dogActionNod": {
                  "0%": { transform: "translateY(0px) scale(1)" },
                  "50%": { transform: "translateY(2px) scale(0.98)" },
                  "100%": { transform: "translateY(0px) scale(1)" },
                },
                "@keyframes dogPlayHop": {
                  "0%": { transform: "translateY(0px) scale(1)" },
                  "50%": { transform: "translateY(-7px) scale(1.03)" },
                  "100%": { transform: "translateY(0px) scale(1)" },
                },
              }}
            >
              <Box
                component="img"
                src={floatingImage}
                alt="Big Dog Bone Puppy"
                sx={{
                  width: { xs: 92, sm: 108 },
                  height: { xs: 92, sm: 108 },
                  objectFit: "contain",
                  filter:
                    pet.status === "fallen"
                      ? "grayscale(1) brightness(0.62)"
                      : "drop-shadow(0 0 10px rgba(255,153,0,0.85))",
                }}
              />
            </Box>
          </Box>
        </Box>
      </Tooltip>

      {/* ======================================================
          MAIN PANEL
      ====================================================== */}
      <Modal open={openPanel} onClose={() => setOpenPanel(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "96%", md: 940 },
            height: { xs: "92vh", md: "82vh" },
            overflow: "hidden",
            background:
              "radial-gradient(circle at top, rgba(255,152,0,0.18), transparent 34%), linear-gradient(180deg, #151515, #050505)",
            border: "2px solid #ff9800",
            borderRadius: "22px",
            boxShadow: "0 0 40px rgba(255,152,0,0.45)",
            p: 1.5,
            color: "white",
            display: "grid",
            gridTemplateRows: "auto auto auto 1fr auto",
            gap: 1,
          }}
        >
          {/* HEADER */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
              alignItems: "flex-start",
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ color: "#ffb300", fontWeight: 900 }}>
                🐶 Big Dog Bone Puppy
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.72)", fontSize: 12 }}>
                Powered by CityFarm resources
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                label={statusText(pet.status)}
                sx={{
                  color: "black",
                  fontWeight: 900,
                  backgroundColor: statusColor(pet.status),
                }}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={openCityFarm}
                sx={{
                  color: "#d5ffd7",
                  borderColor: "#2e7d32",
                  fontWeight: 900,
                }}
              >
                CityFarm
              </Button>
            </Stack>
          </Box>

          {message && (
            <Alert severity={message.type} sx={{ py: 0.4 }}>
              {message.text}
            </Alert>
          )}

          {/* TABS */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Button
              size="small"
              variant={activeTab === "overview" ? "contained" : "outlined"}
              onClick={() => setActiveTab("overview")}
              sx={{
                fontWeight: 900,
                backgroundColor: activeTab === "overview" ? "#ff9800" : "transparent",
                borderColor: "#ff9800",
                color: activeTab === "overview" ? "black" : "#ffcc80",
              }}
            >
              Overview
            </Button>

            <Button
              size="small"
              variant={activeTab === "actions" ? "contained" : "outlined"}
              onClick={() => setActiveTab("actions")}
              sx={{
                fontWeight: 900,
                backgroundColor: activeTab === "actions" ? "#ff9800" : "transparent",
                borderColor: "#ff9800",
                color: activeTab === "actions" ? "black" : "#ffcc80",
              }}
            >
              Actions
            </Button>

            <Button
              size="small"
              variant={activeTab === "guide" ? "contained" : "outlined"}
              onClick={() => setActiveTab("guide")}
              sx={{
                fontWeight: 900,
                backgroundColor: activeTab === "guide" ? "#ff9800" : "transparent",
                borderColor: "#ff9800",
                color: activeTab === "guide" ? "black" : "#ffcc80",
              }}
            >
              Instructions
            </Button>
          </Box>

          {/* BODY */}
          <Box
            sx={{
              minHeight: 0,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "240px 1fr" },
              gap: 1,
              overflow: "hidden",
            }}
          >
            {/* LEFT SIDE */}
            <Box
              sx={{
                minHeight: 0,
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <InfoCard
                title="Puppy"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    width: 170,
                    height: 170,
                    borderRadius: "50%",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                      "radial-gradient(circle at 30% 25%, rgba(255,255,255,0.24), rgba(255,255,255,0.08) 25%, rgba(0,170,255,0.08) 55%, rgba(255,152,0,0.16) 100%)",
                    border: "2px solid rgba(255,255,255,0.24)",
                    boxShadow:
                      "0 0 25px rgba(0,160,255,0.22), 0 0 18px rgba(255,152,0,0.35), inset 0 0 20px rgba(255,255,255,0.08)",
                    mb: 1,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    component="img"
                    src={panelImage}
                    alt="Pet"
                    sx={{
                      width: 140,
                      height: 140,
                      objectFit: "contain",
                      filter:
                        pet.status === "fallen"
                          ? "grayscale(1) brightness(0.62)"
                          : "drop-shadow(0 0 16px rgba(255,153,0,0.85))",
                    }}
                  />
                </Box>

                <Typography sx={{ fontWeight: 900, color: "#ffcc80", fontSize: 15 }}>
                  {displayName}
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: 12 }}>
                  {stageText(pet.stage)}
                </Typography>
              </InfoCard>

              <InfoCard title="Quick Info">
                <SmallRow label="Birth name" value={pet.birthName} />
                <SmallRow label="Level" value={pet.level} />
                <SmallRow label="XP" value={pet.xp} />
                <SmallRow label="Age" value={`${pet.ageDays || 0} days`} />
                <SmallRow label="Status" value={statusText(pet.status)} />
              </InfoCard>

              <InfoCard title="Current Level Benefit">
                <SmallRow label={`Level ${currentLevelBenefit.level}`} value={currentLevelBenefit.benefit} />
                <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: 12 }}>
                  {currentLevelBenefit.daily}
                </Typography>
              </InfoCard>
            </Box>

            {/* RIGHT SIDE */}
            <Box
              sx={{
                minHeight: 0,
                overflow: "auto",
                pr: 0.2,
              }}
            >
              {activeTab === "overview" && (
                <Box>
                  <InfoCard title="Puppy Status">
                    <StatBar label="Life" icon="❤️" value={pet.life?.current || 0} />
                    <StatBar label="Hunger" icon="🍖" value={pet.hunger} />
                    <StatBar label="Thirst" icon="💧" value={pet.thirst} />
                    <StatBar label="Happiness" icon="😊" value={pet.happiness} />
                    <StatBar label="Health" icon="🩺" value={pet.health} />
                    <StatBar label="Energy" icon="⚡" value={pet.energy} />
                  </InfoCard>

                  <Box
                    sx={{
                      mt: 1,
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 1,
                    }}
                  >
                    <InfoCard title="Big Dog Bone">
                      <SmallRow label="Mini-Bone" value={fmtNumber(bdb?.dinero || 0)} />
                      <SmallRow label="Real HUESO" value={fmtNumber(bdb?.dineroReal || 0)} />
                      <SmallRow
                        label="Energy"
                        value={
                          bdb?.energia?.actual !== undefined
                            ? `${fmtNumber(bdb?.energia?.actual)} / ${fmtNumber(
                                bdb?.energia?.maximo
                              )}`
                            : "—"
                        }
                      />
                    </InfoCard>

                    <InfoCard title="CityFarm Resources">
                      <SmallRow label="💧 Water" value={fmtNumber(waterQty)} />
                      <SmallRow label="🍖 Croquettes" value={fmtNumber(foodQty)} />
                      <Button
                        size="small"
                        fullWidth
                        variant="contained"
                        onClick={openCityFarm}
                        sx={{
                          mt: 0.8,
                          backgroundColor: "#2e7d32",
                          fontWeight: 900,
                        }}
                      >
                        Produce More in CityFarm
                      </Button>
                    </InfoCard>

                    <InfoCard title="Current Benefit">
                      <SmallRow
                        label="Status"
                        value={benefit?.active ? "✅ Active" : "❌ Inactive"}
                      />
                      <SmallRow
                        label="Daily claim"
                        value={`${fmtNumber(claimAmount)} Mini-Bone`}
                      />
                      <SmallRow
                        label="Mini-Bone bonus"
                        value={`${benefit?.bdb?.miniHuesoBonusPercent || 0}%`}
                      />
                      <SmallRow
                        label="Energy bonus"
                        value={`${benefit?.bdb?.energyBonusPercent || 0}%`}
                      />
                    </InfoCard>

                    <InfoCard title="Neglect Warning">
                      <Typography sx={{ color: "rgba(255,255,255,0.78)", fontSize: 13 }}>
                        If you neglect your puppy, its stats can drop over time.
                        Weak or fallen states may reduce rewards, and the backend
                        can also reduce XP or level depending on your server rules.
                      </Typography>
                    </InfoCard>
                  </Box>
                </Box>
              )}

              {activeTab === "actions" && (
                <Box>
                  {canUseActions ? (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: 1,
                      }}
                    >
                      <Button
                        disabled={loading}
                        variant="contained"
                        onClick={feedPet}
                        sx={{ backgroundColor: "#ff9800", fontWeight: 900 }}
                      >
                        🍖 Feed
                      </Button>

                      <Button
                        disabled={loading}
                        variant="contained"
                        onClick={waterPet}
                        sx={{ backgroundColor: "#2196f3", fontWeight: 900 }}
                      >
                        💧 Give Water
                      </Button>

                      <Button
                        disabled={loading}
                        variant="contained"
                        onClick={playPet}
                        sx={{ backgroundColor: "#4caf50", fontWeight: 900 }}
                      >
                        🎾 Play
                      </Button>

                      <Button
                        disabled={loading}
                        variant="contained"
                        onClick={claimDaily}
                        sx={{ backgroundColor: "#9c27b0", fontWeight: 900 }}
                      >
                        🦴 Claim Daily
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{ display: "grid", gap: 1 }}>
                      <Alert severity="error">
                        Your puppy is fallen. Revive it to recover benefits.
                      </Alert>

                      <Button
                        disabled={loading}
                        variant="contained"
                        onClick={() => revivePet("dinero")}
                        sx={{ backgroundColor: "#ff9800", fontWeight: 900 }}
                      >
                        Revive with Mini-Bone
                      </Button>

                      <Button
                        disabled={loading}
                        variant="contained"
                        onClick={() => revivePet("dineroReal")}
                        sx={{ backgroundColor: "#f44336", fontWeight: 900 }}
                      >
                        Revive with Real HUESO
                      </Button>
                    </Box>
                  )}

                  <Box
                    sx={{
                      mt: 1,
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 1,
                    }}
                  >
                    <InfoCard title="Cooldowns">
                      <SmallRow label="Feed" value={cooldowns?.feed?.text || "Ready"} />
                      <SmallRow label="Water" value={cooldowns?.water?.text || "Ready"} />
                      <SmallRow label="Play" value={cooldowns?.play?.text || "Ready"} />
                      <SmallRow label="Claim" value={cooldowns?.claim?.text || "Ready"} />
                    </InfoCard>

                    <InfoCard title="Nickname">
                      <TextField
                        size="small"
                        fullWidth
                        value={nickname}
                        onChange={(e) => setNickname(e.target.value)}
                        placeholder="Puppy nickname"
                        sx={{
                          input: { color: "white" },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": { borderColor: "#ff9800" },
                            "&:hover fieldset": { borderColor: "#ffb300" },
                            "&.Mui-focused fieldset": { borderColor: "#ffcc80" },
                          },
                        }}
                      />

                      <Button
                        fullWidth
                        disabled={loading}
                        variant="outlined"
                        onClick={saveNickname}
                        sx={{
                          mt: 0.8,
                          color: "#ffb300",
                          borderColor: "#ffb300",
                          fontWeight: 900,
                        }}
                      >
                        Save Nickname
                      </Button>
                    </InfoCard>
                  </Box>

                  <InfoCard title="Tips" sx={{ mt: 1 }}>
                    <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: 13 }}>
                      Action images are now preloaded and stay visible longer.
                      Feed, Water, and Play should now display more clearly.
                    </Typography>
                  </InfoCard>
                </Box>
              )}

              {activeTab === "guide" && (
                <Box>
                  <Alert severity="info" sx={{ mb: 1 }}>
                    Your puppy lives in Big Dog Bone, but it uses Water and Croquettes produced in CityFarm.
                  </Alert>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 1,
                    }}
                  >
                    <InfoCard title="Daily Care">
                      <SmallRow label="Feed" value="Uses 1 Croquettes from CityFarm" />
                      <SmallRow label="Water" value="Uses 1 Water from CityFarm" />
                      <SmallRow label="Play" value="Improves happiness" />
                      <SmallRow label="Daily claim" value="Gives Mini-Bone when conditions are met" />
                    </InfoCard>

                    <InfoCard title="How to Keep Benefits Active">
                      <SmallRow label="Hunger" value="Keep above 55%" />
                      <SmallRow label="Thirst" value="Keep above 55%" />
                      <SmallRow label="Happiness" value="Keep above 45%" />
                      <SmallRow label="Health" value="Keep above 65%" />
                      <SmallRow label="Life" value="Keep above 25%" />
                    </InfoCard>
                  </Box>

                  <InfoCard title="Neglect Rule" sx={{ mt: 1 }}>
                    <Typography sx={{ color: "rgba(255,255,255,0.76)", fontSize: 13 }}>
                      If the puppy is forgotten, hunger, thirst, happiness, health,
                      and life go down over time. If it reaches a critical state,
                      it can become weak or fallen. XP or level loss must be enforced
                      by the backend for full security.
                    </Typography>
                  </InfoCard>

                  <InfoCard title="Benefits by Level" sx={{ mt: 1 }}>
                    {benefitLevels.map((row, index) => (
                      <Box key={row.level}>
                        <SmallRow label={`Level ${row.level}`} value={row.benefit} />
                        <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 12, mb: 0.6 }}>
                          {row.daily}
                        </Typography>
                        {index !== benefitLevels.length - 1 && (
                          <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mb: 0.6 }} />
                        )}
                      </Box>
                    ))}
                  </InfoCard>

                  <Button
                    variant="contained"
                    onClick={openCityFarm}
                    sx={{
                      mt: 1,
                      backgroundColor: "#2e7d32",
                      fontWeight: 900,
                    }}
                  >
                    Open CityFarm to Produce Water and Croquettes
                  </Button>
                </Box>
              )}
            </Box>
          </Box>

          {/* FOOTER */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 12 }}>
              {lastUpdated
                ? `Updated: ${lastUpdated.toLocaleTimeString()}`
                : "Not updated yet"}
            </Typography>

            <Button
              disabled={loading}
              variant="text"
              onClick={() =>
                refreshPet({
                  silent: false,
                  keepMessage: false,
                })
              }
              sx={{ color: "#ffcc80", fontWeight: 900 }}
            >
              {loading ? "Updating..." : "Refresh All"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}